import {Component, Input, OnInit, ViewChild} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {
  ItemAvailability,
  V3Exam,
  V3ExamSession,
  V3ExamSessionPart,
  V3Exercise,
  V3ExerciseSession,
  V3Section
} from '../../model/v3/cspa';
import {NativeServiceApiProvider} from "../../../services/native-api-provider.service";
import {combineLatest, map, of, switchMap, zip} from "rxjs";
import {ModalComponent} from 'src/app/utils/modal/modal.component';
import {AuthorizationServiceProvider} from "../../../auth_profile/services/authorization-service.provider";

@Component({
    selector: 'question-set',
    templateUrl: './question-set.component.html',
    styleUrls: ['./question-set.component.scss'],
    standalone: false
})
export class QuestionSetComponent implements OnInit {
  isMobile = true

  @ViewChild('instructionModal', {static: true})
    instructionModal: ModalComponent;

  @ViewChild('lockedExerciseSetModal', { static: true })
    lockedExerciseSetModal: ModalComponent;
  @ViewChild('completedExamResultModal', { static: true })
    completedExamResultModal: ModalComponent;

  @Input() section?: V3Section;
  @Input() availabilitiesMap?: Map<string,ItemAvailability>;
  chapter: string
  completedExamSession: V3ExamSession
  completedExamSessionParts: V3ExamSessionPart[]
  isCompletedExamResultLoading: boolean = false
  isCompletedExamResultError: boolean = false
  personId: number


  constructor(
    private router: Router,
    private provider: NativeServiceApiProvider,
    private route: ActivatedRoute,
    private auth: AuthorizationServiceProvider,
  ) {
    this.auth.getCommonRoutines().subscribe(
      api => {
        this.isMobile = api.isNativeAuthorization()
      }
    )
  }

  ngOnInit(): void {
    combineLatest([
      this.route.paramMap,
      this.auth.getAuthDetailsService().pipe(
        switchMap(authDetailsApi => authDetailsApi.getSelfPersonId())
      )
    ]).subscribe(([params, personId]) => {
      this.chapter = params.get("chapter")
      this.personId = personId
    });
  }

  getFullType() {
    return this.section.name
  }

  getShortName() {
    return this.section.name.split(',')[0]
  }

  getStage(){
    return this.section.name.split(',')[1]
  }

  getDescriptionCode() {
    return this.section.type.toUpperCase()
  }

  getAccentColor() {
    return `stg-${this.section?.name.slice(-2).trim()}`
  }

  getExercises() {
    return this.section.nested
  }

  getAvailability(exercise: { path: string }) {
    return [...this.availabilitiesMap.values()].filter(av =>
      av.path.endsWith(exercise.path))[0]
  }

  onContinue() {
    for (const ex of this.getExercises()) {
      //find the first with bad score or never done
      if(this.getAvailability(ex).score < 0.5 || this.getAvailability(ex).score === 0)
        return this.startSession(ex)
        //find the first with good score but not perfect (below full green)
      else if(this.getAvailability(ex).score >= 0.5 && this.getAvailability(ex).score < 1)
        return this.startSession(ex)
    }
    //find the one which is the oldest lastUpdate
    const ex = this.getExercises().sort((aKey, bKey) =>
    aKey.lastUpdate - bKey.lastUpdate)[0]
    return this.startSession(ex)
  }

  startSession(exercise: V3Exercise | V3Exam) {
    if (!this.getAvailability(exercise).isAssigned || !this.getAvailability(exercise).isAvailable) {
      this.lockedExerciseSetModal.show(true);
      return
    }
    if (this.isExam(exercise) && this.isCompletedRealExam(exercise)) {
      this.openCompletedExamResultModal(exercise)
      return
    }
    this.isExam(exercise)
      ? this.startExamSession(exercise)
      : this.startExerciseSession(exercise)
  }

  isExamOnMobile() {
    return this.section.type == 'V3Exam' && this.isMobile
  }

  isCompletedRealExam(exercise: V3Exercise | V3Exam) {
    const availability = this.getAvailability(exercise)
    return this.isExam(exercise) && exercise.path !== 'ex0' && availability?.progress == 1
  }

  hasCompletedRealExam() {
    if (this.section?.type != 'V3Exam') {
      return false
    }
    return this.getExercises().some(exercise => this.isCompletedRealExam(exercise))
  }

  openCompletedExamResultModal(exam: V3Exam) {
    this.completedExamSession = null
    this.completedExamSessionParts = null
    this.isCompletedExamResultLoading = true
    this.isCompletedExamResultError = false
    this.completedExamResultModal.show(true)

    this.provider.cspaV3().pipe(
      switchMap(api => api.findExamSessions(this.personId, exam.path).pipe(
        map(sessions => this.getLatestFinishedExamSession(sessions, exam.path)),
        switchMap(session => session?.uuid
          ? zip(of(session), api.findExamSessionsParts(this.personId, session.uuid))
          : of([session, []] as [V3ExamSession, V3ExamSessionPart[]]))
      )),
    ).subscribe({
      next: ([session, sessionParts]) => {
        this.completedExamSession = session
        this.completedExamSessionParts = sessionParts
        this.isCompletedExamResultLoading = false
        this.isCompletedExamResultError = !session
      },
      error: _ => {
        this.isCompletedExamResultLoading = false
        this.isCompletedExamResultError = true
      }
    })
  }

  getLatestFinishedExamSession(sessions: V3ExamSession[], examPath: string) {
    return sessions
      ?.filter(session => session?.path === examPath || session?.path?.startsWith(`${examPath}_`))
      ?.filter(session => this.isFinishedExamSession(session))
      ?.sort((left, right) => this.toMillis(right?.finishDate || right?.lastUpdateDate)
        - this.toMillis(left?.finishDate || left?.lastUpdateDate))[0]
  }

  isFinishedExamSession(session: V3ExamSession) {
    return !!session?.finishDate
  }

  toMillis(date: Date | string) {
    const value = new Date(date).getTime()
    return isNaN(value) ? 0 : value
  }

  getCompletedExamTotalScore() {
    return this.completedExamSessionParts?.reduce((sum, part) => sum + (part?.score * part?.weight), 0)
  }

  getCompletedExamStageNumber(): number {
    return parseInt(this.completedExamSession?.chapterName?.charAt(this.completedExamSession?.chapterName?.length - 1))
  }

  getCompletedExamStageRange(): string {
    return this.getCompletedExamStageNumber() < 3 ? 'STAGE_1_2' : 'STAGE_3_12'
  }

  private startExerciseSession(exercise: V3Exercise) {
    this.provider
      .cspaV3()
      .pipe(switchMap((api) => api.createSession(this.personId, {path: exercise.path})))
      .subscribe((session) => {
        this.routeToTheSession(session, exercise);
      });
  }

  private routeToTheSession(session: V3ExerciseSession, exercise: V3Exercise) {
    this.router.navigate(
      [`${this.section.path}/${exercise.path}/session/${session.deviceUUID}/1`],
      {
        relativeTo: this.route,
        queryParamsHandling: 'merge'
      },
    ).then()
  }

  private startExamSession(exam: V3Exam) {
    this.provider
      .cspaV3()
      .pipe(switchMap((api) => api.createExamSession(this.personId, {path: exam.path})))
      .subscribe((session) => {
        this.routeToTheExamSession(session, exam);
      });
  }

  private routeToTheExamSession(session: V3ExamSession, exam: V3Exam) {
    this.router.navigate(
      [`${this.section.path}/${exam.path}/exam-session/${session?.uuid}`],
      {
        relativeTo: this.route,
        queryParamsHandling: 'merge'
      },
    ).then()
  }

  private isExam(exercise: V3Exercise | V3Exam): exercise is V3Exam {
    return exercise['@type'] === 'V3Exam'
  }
}
